/**
 * 数据探索页面 - 主页面
 * 提供原始反馈数据的浏览、筛选、查看和导出功能
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Card, App, Spin, Button } from 'antd';
import { DownloadOutlined, CloseCircleFilled } from '@ant-design/icons';
import VocDataTable from './components/VocDataTable';
import { getAllVocData } from '../../services/vocDataService';
import type { VocRecord } from '../../types/vocData';
import './DataExplorer.css';

// 字段名称映射
const fieldNameMap: Record<string, string> = {
  journey: '用户旅程',
  task: '任务',
  subTask: '子任务',
  bestPractice: '最佳体验项',
  issueTag: '情感',
};

const DataExplorer: React.FC = () => {
  const { message } = App.useApp();
  const [allData, setAllData] = useState<VocRecord[]>([]);
  const [filteredData, setFilteredData] = useState<VocRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getAllVocData();
        setAllData(data);
        setFilteredData(data);
      } catch (error) {
        message.error('加载数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 处理表格筛选变化
  const handleFilterChange = useCallback((filtered: VocRecord[]) => {
    setFilteredData(filtered);
  }, []);

  // 导出 CSV
  const handleExport = useCallback(() => {
    if (filteredData.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    message.loading({ content: '正在导出...', key: 'export', duration: 0 });

    try {
      const headers = [
        'VOC ID',
        'PN',
        '原文',
        '切片',
        '翻译',
        '用户旅程',
        '任务',
        '子任务',
        '最佳体验项',
        '问题标签',
        '星级',
      ];

      const csvContent = [
        headers.join(','),
        ...filteredData.map((record) =>
          [
            record.vocId,
            record.pn,
            `"${record.original.replace(/"/g, '""')}"`,
            `"${record.slice.replace(/"/g, '""')}"`,
            `"${record.translation.replace(/"/g, '""')}"`,
            record.journey,
            record.task,
            record.subTask,
            `"${record.bestPractice.replace(/"/g, '""')}"`,
            record.issueTag,
            record.starRating ?? '',
          ].join(',')
        ),
      ].join('\n');

      // 创建下载链接
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `VOC数据导出_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success({ content: `成功导出 ${filteredData.length} 条数据！`, key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败，请重试', key: 'export' });
    }
  }, [filteredData]);

  if (loading) {
    return (
      <div className="data-explorer loading-container">
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666' }}>加载数据中...</div>
      </div>
    );
  }

  return (
    <div className="data-explorer">
      {/* 顶部操作栏 */}
      <div className="data-explorer-header">
        <div className="header-info">
          <span className="data-count">
            总计 <strong>{allData.length}</strong> 条数据
            {filteredData.length < allData.length && (
              <span className="filtered-count">
                {' '}
                | 筛选后 <strong>{filteredData.length}</strong> 条
              </span>
            )}
          </span>
          {Object.values(activeFilters).some((f) => f.length > 0) && (
            <div className="active-filter-tags">
              {Object.entries(activeFilters)
                .filter(([, values]) => values.length > 0)
                .map(([field, values]) => (
                  <span key={field} className="active-filter-tag">
                    {fieldNameMap[field] || field}: {values.join(', ')}
                    <CloseCircleFilled
                      className="active-filter-tag-close"
                      onClick={() => {
                        const newFilters: Record<string, string[]> = { ...activeFilters, [field]: [] };
                        setActiveFilters(newFilters);
                      }}
                    />
                  </span>
                ))}
            </div>
          )}
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          disabled={filteredData.length === 0}
        />
      </div>

      {/* 数据表格 */}
      <Card className="table-card">
        <VocDataTable
          data={allData}
          loading={false}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onActiveFiltersChange={setActiveFilters}
        />
      </Card>
    </div>
  );
};

export default DataExplorer;
